import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../api';
import toast from 'react-hot-toast';

interface User {
  id: string; email: string; businessName: string; companyName: string;
  phone: string; subscriptionTier: string; subscriptionStatus: string;
  createdAt: string; lastQuoteSentAt: string; setupComplete: boolean;
  _count: { quotes: number; templates: number; customers: number };
}
interface Stats {
  totalUsers: number; activeUsers: number; totalQuotes: number;
  mrr: number; churnedThisMonth: number; trialUsers: number;
  tierBreakdown: { free: number; starter: number; pro: number; agency: number };
  quotesThisMonth: number; newUsersThisMonth: number;
}

const card: any = { background: 'white', borderRadius: 12, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' };
const fmt = (c: number) => `$${(c / 100).toFixed(2)}`;
const tierColor = (t: string) =>
  t === 'agency'  ? { background: '#fef3c7', color: '#92400e' } :
  t === 'pro'     ? { background: '#dbeafe', color: '#1d4ed8' } :
  t === 'starter' ? { background: '#dcfce7', color: '#16a34a' } :
                    { background: '#f3f4f6', color: '#555' };

export default function Admin() {
  const [stats,  setStats]  = useState<Stats | null>(null);
  const [users,  setUsers]  = useState<User[]>([]);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return navigate('/login') as any;
    Promise.all([
      api.get('/admin/stats'),
      api.get('/admin/users')
    ]).then(([s, u]) => {
      setStats(s.data);
      setUsers(u.data);
    }).catch(err => {
      if (err.response?.status === 403) {
        toast.error('Admin access only');
        navigate('/dashboard');
      }
    }).finally(() => setLoading(false));
  }, []);

  const filteredUsers = users.filter(u => {
    const matchSearch = !search ||
      u.email.toLowerCase().includes(search.toLowerCase()) ||
      (u.businessName || '').toLowerCase().includes(search.toLowerCase()) ||
      (u.companyName  || '').toLowerCase().includes(search.toLowerCase());
    const matchFilter =
      filter === 'all'     ? true :
      filter === 'paid'    ? ['starter','pro','agency'].includes(u.subscriptionTier) :
      filter === 'trial'   ? u.subscriptionStatus === 'trialing' :
      filter === 'active'  ? !!u.lastQuoteSentAt :
      filter === 'setup'   ? !u.setupComplete :
      u.subscriptionTier === filter;
    return matchSearch && matchFilter;
  });

  const changeTier = async (userId: string, tier: string) => {
    try {
      await api.put(`/admin/users/${userId}`, { subscriptionTier: tier });
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, subscriptionTier: tier } : u));
      toast.success('Tier updated');
    } catch { toast.error('Failed to update'); }
  };

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <p style={{ color: '#666' }}>Loading admin dashboard...</p>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#0f172a' }}>
      {/* Nav */}
      <nav style={{ background: '#1e293b', padding: '16px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #334155' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: '#f1f5f9', margin: 0 }}>⚙️ QuotationBot Admin</h1>
          <span style={{ background: '#dc2626', color: 'white', fontSize: 11, padding: '2px 8px', borderRadius: 12, fontWeight: 700 }}>ADMIN</span>
        </div>
        <Link to="/dashboard" style={{ color: '#94a3b8', textDecoration: 'none', fontSize: 14 }}>← Back to App</Link>
      </nav>

      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '32px 24px' }}>

        {/* ── KPI Cards ── */}
        {stats && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 32 }}>
            {[
              { label: 'Monthly Recurring Revenue', value: fmt(stats.mrr * 100), sub: `${stats.activeUsers} paying users`, color: '#10b981' },
              { label: 'Total Users', value: stats.totalUsers, sub: `+${stats.newUsersThisMonth} this month`, color: '#3b82f6' },
              { label: 'Quotes This Month', value: stats.quotesThisMonth, sub: `${stats.totalQuotes} all time`, color: '#8b5cf6' },
              { label: 'Trial Users', value: stats.trialUsers, sub: `${stats.churnedThisMonth} churned this month`, color: '#f59e0b' },
            ].map(({ label, value, sub, color }) => (
              <div key={label} style={{ background: '#1e293b', borderRadius: 12, padding: 20, border: '1px solid #334155' }}>
                <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 8 }}>{label}</div>
                <div style={{ fontSize: 28, fontWeight: 700, color, marginBottom: 4 }}>{value}</div>
                <div style={{ fontSize: 12, color: '#64748b' }}>{sub}</div>
              </div>
            ))}
          </div>
        )}

        {/* ── Tier Breakdown ── */}
        {stats && (
          <div style={{ background: '#1e293b', borderRadius: 12, padding: 20, border: '1px solid #334155', marginBottom: 32 }}>
            <h3 style={{ color: '#f1f5f9', marginBottom: 16, fontSize: 15 }}>Plan Distribution</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
              {[
                { label: 'Free',    count: stats.tierBreakdown.free,    color: '#64748b' },
                { label: 'Starter', count: stats.tierBreakdown.starter, color: '#10b981' },
                { label: 'Pro',     count: stats.tierBreakdown.pro,     color: '#3b82f6' },
                { label: 'Agency',  count: stats.tierBreakdown.agency,  color: '#f59e0b' },
              ].map(({ label, count, color }) => (
                <div key={label} style={{ textAlign: 'center', padding: '12px', background: '#0f172a', borderRadius: 8 }}>
                  <div style={{ fontSize: 24, fontWeight: 700, color }}>{count}</div>
                  <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>{label}</div>
                  <div style={{ fontSize: 11, color: '#475569' }}>{stats.totalUsers ? Math.round((count / stats.totalUsers) * 100) : 0}%</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Users Table ── */}
        <div style={{ background: '#1e293b', borderRadius: 12, border: '1px solid #334155', overflow: 'hidden' }}>
          <div style={{ padding: '20px 24px', borderBottom: '1px solid #334155', display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <h3 style={{ color: '#f1f5f9', margin: 0, fontSize: 15, flex: 1 }}>
              Users ({filteredUsers.length})
            </h3>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by email or name..."
              style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #334155', background: '#0f172a', color: '#f1f5f9', fontSize: 14, width: 220 }}
            />
            <select value={filter} onChange={e => setFilter(e.target.value)}
              style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #334155', background: '#0f172a', color: '#f1f5f9', fontSize: 14 }}>
              <option value="all">All users</option>
              <option value="paid">Paid only</option>
              <option value="trial">On trial</option>
              <option value="active">Active (sent quote)</option>
              <option value="setup">Not set up</option>
              <option value="free">Free tier</option>
              <option value="starter">Starter</option>
              <option value="pro">Pro</option>
              <option value="agency">Agency</option>
            </select>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#0f172a' }}>
                  {['User', 'Plan', 'Quotes', 'Status', 'Last Active', 'Joined', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 12, color: '#64748b', fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map(u => (
                  <tr key={u.id} style={{ borderTop: '1px solid #334155' }}>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ fontWeight: 600, color: '#f1f5f9', fontSize: 14 }}>
                        {u.companyName || u.businessName || '—'}
                      </div>
                      <div style={{ fontSize: 12, color: '#64748b' }}>{u.email}</div>
                      {u.phone && <div style={{ fontSize: 11, color: '#475569' }}>{u.phone}</div>}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <select
                        value={u.subscriptionTier}
                        onChange={e => changeTier(u.id, e.target.value)}
                        style={{ padding: '4px 8px', borderRadius: 6, border: 'none', fontSize: 12, fontWeight: 700, cursor: 'pointer', ...tierColor(u.subscriptionTier) }}>
                        <option value="free">Free</option>
                        <option value="starter">Starter</option>
                        <option value="pro">Pro</option>
                        <option value="agency">Agency</option>
                      </select>
                    </td>
                    <td style={{ padding: '12px 16px', color: '#94a3b8', fontSize: 14 }}>
                      <div>{u._count.quotes} quotes</div>
                      <div style={{ fontSize: 12, color: '#475569' }}>{u._count.customers} customers</div>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ fontSize: 12, padding: '3px 8px', borderRadius: 12,
                        background: u.subscriptionStatus === 'active' ? '#dcfce7' :
                                    u.subscriptionStatus === 'trialing' ? '#fef3c7' : '#fee2e2',
                        color: u.subscriptionStatus === 'active' ? '#16a34a' :
                               u.subscriptionStatus === 'trialing' ? '#92400e' : '#dc2626'
                      }}>
                        {u.subscriptionStatus}
                      </span>
                      {!u.setupComplete && (
                        <div style={{ fontSize: 11, color: '#f59e0b', marginTop: 4 }}>Not set up</div>
                      )}
                    </td>
                    <td style={{ padding: '12px 16px', color: '#94a3b8', fontSize: 13 }}>
                      {u.lastQuoteSentAt
                        ? new Date(u.lastQuoteSentAt).toLocaleDateString()
                        : <span style={{ color: '#475569' }}>Never</span>}
                    </td>
                    <td style={{ padding: '12px 16px', color: '#64748b', fontSize: 13 }}>
                      {new Date(u.createdAt).toLocaleDateString()}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <button
                        onClick={() => { if(window.confirm(`Impersonate ${u.email}?`)) toast('Impersonation coming soon'); }}
                        style={{ fontSize: 11, padding: '4px 10px', background: '#334155', border: 'none', borderRadius: 6, cursor: 'pointer', color: '#94a3b8' }}>
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredUsers.length === 0 && (
              <div style={{ padding: 40, textAlign: 'center', color: '#475569' }}>No users found</div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}